/**
 * The numbers that make a McLeod screen feel native on a phone or handheld.
 *
 * Each value is chosen to satisfy both platform guides at once, so one set of tokens serves Android and iOS:
 *   - Material 3: 48dp touch targets, 40dp button height (56dp text fields), 12-16dp component radii, body text
 *     16sp, headline 24-32sp.
 *   - Apple Human Interface Guidelines: 44pt minimum hit target, 17pt body, 11pt captions, rounded rectangles,
 *     large titles 34pt.
 * Where the guides disagree the larger value wins, because these apps run on gloved-hand warehouse devices.
 *
 * Use these from TypeScript when building components by hand; layouts deserialized in the app get them applied
 * automatically through the App* component types (AppButton, AppTextbox, AppTable, ...).  See
 * mcleod-ui-common-mobile/docs/mobile-design-guidelines.md for how the pieces fit together.
 */
export const MobileDesignTokens = {
    /** Minimum size of anything the user taps.  HIG 44pt; Material recommends 48dp including surrounding padding. */
    touchTarget: {
        min: 44,
        comfortable: 48
    },

    /** Full-width primary/secondary buttons and the icon-only buttons in headers. */
    button: {
        height: 48,
        minHeight: 44,
        radius: 12,
        /** 16px reads at arm's length and matches Material's label-large / HIG body. */
        fontSize: "large",
        paddingHorizontal: 20,
        iconSize: 24
    },

    /** Single-line text entry. */
    textField: {
        /** Material filled fields are 56dp, HIG fields 44pt; 48 is the comfortable middle for dense forms. */
        height: 48,
        radius: 10,
        /** Inputs below 16px make iOS Safari/WKWebView zoom the page when they gain focus. */
        fontSize: "large",
        captionFontSize: "medium"
    },

    /** Table rows and other list items. */
    listItem: {
        minHeight: 56,
        paddingHorizontal: 16
    },

    /** Spacing scale (Material uses 4dp increments; HIG typically 8/16/20). */
    spacing: {
        xs: 4,
        sm: 8,
        md: 12,
        lg: 16,
        xl: 24
    },

    /** McLeod theme font-size names that map to sensible mobile type roles. */
    typography: {
        largeTitle: "xxxlarge",  // 24px  (HIG large title is 34pt; 24 fits a 360dp handheld)
        title: "xxlarge",        // 20px
        body: "large",           // 16px
        secondary: "medium",     // 14px
        caption: "small"         // 12px
    },

    /** Surfaces. */
    sheet: { cornerRadius: 20, grabberWidth: 36, grabberHeight: 4 },
    card: { cornerRadius: 16 }
} as const;
